import React, { useState, useCallback, useRef, useEffect } from "react";
import styles from "./index.module.css";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { useDispatch, useSelector } from "react-redux";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { addBulkData } from "../../../redux/features/bulkRegistration/bulkRegistrationService";
import { getAllLotteryBrands } from "../../../redux/features/lotteryBrand/lotteryBrandService";
import { getAllLotteryBrandDaysCode } from "../../../redux/features/lotteryBrandDay/lotteryBrandDayService";

const BulkRegistration = () => {
  const dispatch = useDispatch();
  const brandDropdownRef = useRef(null);
  const brandDateDropdownRef = useRef(null);

  const { lotteries } = useSelector((state) => state.lottery.allLotteryBrands);

  const { isLotteryCreating } = useSelector(
    (state) => state.bulkRegistration.addBulk
  );

  const { lotteryBrandDayCode } = useSelector(
    (state) => state.lotteryBrandDay.allLotteryBrandDayCode
  );

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    drawNo: "",
    drawDate: "",
    megaPrice: "",
    file: null,
    brandDate: "",
    start_serial: "",
    end_serial: "",
    count: "",
    target_count: "",
    price: "",
  });

  const [errorMessages, setErrorMessages] = useState({
    drawNo: "",
    drawDate: "",
    megaPrice: "",
    file: "",
    brandDate: "",
    start_serial: "",
    end_serial: "",
    count: "",
    target_count: "",
    price: "",
  });
  const [brand, setBrand] = useState(null);
  const [disabledBtn, setBtnDisable] = useState(false);

  const fileInputRef = useRef(null);

  const fetchAllLotteries = useCallback(() => {
    dispatch(getAllLotteryBrands({ status: true }));
  }, [dispatch]);

  useEffect(() => {
    fetchAllLotteries();
  }, [fetchAllLotteries]);

  const handleLotteryBrand = (option) => {
    setBrand(option.code);
  };

  const handleLotteryBrandCode = (option) => {
    setFormData({ ...formData, brandDate: option.id });
    setErrorMessages({ brandDate: "" });
  };

  useEffect(() => {
    dispatch(getAllLotteryBrandDaysCode({ id: brand }));
  }, [dispatch, brand]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.dbf')) {
      setError("Invalid file type. Please upload a DBF file.");
      setFormData({ ...formData, file: null });
      return;
    }

    setFormData({ ...formData, file: e.target.files[0] });
    setError(null);
    setErrorMessages({ ...errorMessages, file: "" });
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files[0];
    
    if (droppedFile && droppedFile.name.split(".").pop().toLowerCase() === "dbf") {
      handleFileChange({ target: { files: [droppedFile] } });
      setError(null);
    } else {
      setError("Invalid file type. Please upload a DBF file.");
    }
  });

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrorMessages((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleLabelClick = (event) => {
    event.stopPropagation();
    fileInputRef.current.click();
  };

  // Validate input
  const isValidLotteryBulk = () => {
    const errors = {};
    const trimmedNumber = formData.drawNo.trim();
    const trimmedDate = formData.drawDate.trim();
    const trimmedPrice = formData.megaPrice.trim();
    const trimmedBrandDate = formData.brandDate.trim();
    const trimmedStartSerial = formData.start_serial.trim();
    const trimmedEndSerial = formData.end_serial.trim();
    const trimmedCount = formData.count.trim();
    const trimmedTargetCount = formData.target_count.trim();
    const trimmedPriceValue = formData.price.trim();

    if (!trimmedNumber) {
      errors.drawNo = "Draw number is required.";
    }
    if (!trimmedDate) {
      errors.drawDate = "Draw date is required.";
    }
    if (!trimmedPrice) {
      errors.megaPrice = "Mega Price is required.";
    }
    if (!formData.file) {
      errors.file = "File is required.";
    }
    if (!trimmedBrandDate) {
      errors.brandDate = "Brand date is required.";
    }
    if (!trimmedStartSerial) {
      errors.start_serial = "Start serial number is required.";
    }
    if (!trimmedEndSerial) {
      errors.end_serial = "End Serial number is required.";
    }
    if (trimmedStartSerial && trimmedEndSerial) {
      if (parseInt(trimmedStartSerial) >= parseInt(trimmedEndSerial)) {
        errors.end_serial = "End Serial number should be greater than start serial number.";
      }
    }
    if (!trimmedCount) {
      errors.count = "Count is required.";
    }
    if (!trimmedTargetCount) {
      errors.target_count = "Target Count is required.";
    }
    if (!trimmedPriceValue) {
      errors.price = "Price is required.";
    }

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return false;
    } else {
      return true;
    }
  };

  const handleClick = (ref) => {
    ref.current.click();
  };

  const handleBulkRegistration = async () => {
    if (isValidLotteryBulk(formData)) {
      setBtnDisable(true);
      try {
        const response = await dispatch(addBulkData(formData)).unwrap();
        setBtnDisable(false);
        setFormData({
          drawNo: "",
          drawDate: "",
          megaPrice: "",
          file: null,
          brand: "",
          brandDate: "",
          start_serial: "",
          end_serial: "",
          count: "",
          target_count: "",
          price: "",
        });
        fileInputRef.current.value = "";
        brandDateDropdownRef.current?.clearSelection();
        brandDropdownRef.current.clearSelection();
        showToast("success", response.message);
        setData(null);
      } catch (error) {
        setBtnDisable(false);
        showToast("error", error.message);
      }
    }
  };

  return (
    <div className={`card-section`}>
      <div className={`main-card`}>
        <h1 className={`form-title`}>New Tickets </h1>
        <div className={styles.formContainer}>
          <form className={styles.coordinatorForm} noValidate>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="brand" className={`form-label`}>
                    Brand<span className={`required-star`}>*</span>
                  </label>
                  <Dropdown
                    ref={brandDropdownRef}
                    options={lotteries}
                    placeholder="Select Lottery Brand"
                    onSelect={handleLotteryBrand}
                  />
                  {errorMessages.brand && (
                    <p className={`text-danger`}>{errorMessages.brand}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="brandDate" className={`form-label`}>
                    Lottery Brand Day<span className={`required-star`}>*</span>
                  </label>
                  <Dropdown
                    ref={brandDateDropdownRef}
                    options={lotteryBrandDayCode}
                    placeholder="Select Lottery Brand Day"
                    onSelect={handleLotteryBrandCode}
                  />
                  {errorMessages.brandDate && (
                    <p className={`text-danger`}>{errorMessages.brandDate}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="drawNo" className={`form-label`}>
                    Draw Number<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="drawNo"
                    name="drawNo"
                    value={formData.drawNo}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder="Enter Draw No"
                    className={`form-input-default`}
                    min={1}
                  />
                  {errorMessages.drawNo && (
                    <p className={`text-danger`}>{errorMessages.drawNo}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="drawDate" className={`form-label`}>
                    Draw Date<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="date"
                    id="drawDate"
                    name="drawDate"
                    value={formData.drawDate}
                    onChange={handleInputChange}
                    placeholder="Enter Lottery Draw Date"
                    className={`form-input-default`}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {errorMessages.drawDate && (
                    <p className={`text-danger`}>{errorMessages.drawDate}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="megaPrice" className={`form-label`}>
                    Mega Prize<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="megaPrice"
                    name="megaPrice"
                    value={formData.megaPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder="Enter Mega Prize"
                    className={`form-input-default`}
                    min={0}
                  />
                  {errorMessages.megaPrice && (
                    <p className={`text-danger`}>{errorMessages.megaPrice}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="start_serial" className={`form-label`}>
                    Start Serial Number
                    <span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="start_serial"
                    name="start_serial"
                    value={formData.start_serial}
                    onChange={handleInputChange}
                    placeholder="Enter Start Serial Number"
                    className={`form-input-default`}
                  />
                  {errorMessages.start_serial && (
                    <p className={`text-danger`}>
                      {errorMessages.start_serial}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="end_serial" className={`form-label`}>
                    End Serial Number<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="end_serial"
                    name="end_serial"
                    value={formData.end_serial}
                    onChange={handleInputChange}
                    placeholder="Enter End Serial Number"
                    className={`form-input-default`}
                  />
                  {errorMessages.end_serial && (
                    <p className={`text-danger`}>{errorMessages.end_serial}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="count" className={`form-label`}>
                    Count<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="count"
                    name="count"
                    value={formData.count}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder="Enter count"
                    className={`form-input-default`}
                  />
                  {errorMessages.count && (
                    <p className={`text-danger`}>{errorMessages.count}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="target_count" className={`form-label`}>
                    Target Count <span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="target_count"
                    name="target_count"
                    value={formData.target_count}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder="Enter target count"
                    className={`form-input-default`}
                  />
                  {errorMessages.target_count && (
                    <p className={`text-danger`}>
                      {errorMessages.target_count}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="price" className={`form-label`}>
                    Price <span className={`required-star`}>*</span>
                  </label>
                  <input
                    type="text"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder="Enter price"
                    className={`form-input-default`}
                  />
                  {errorMessages.price && (
                    <p className={`text-danger`}>
                      {errorMessages.price}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor="fileInput" className={`form-label`}>
                    File<span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => handleClick(fileInputRef)}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      id="fileInput"
                      ref={fileInputRef}
                      accept=".dbf"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    <label onClick={handleLabelClick}>
                      {formData.file
                        ? formData.file.name
                        : <>
                          <h2 className={styles.bannerText}>File</h2>
                          <p className={styles.dragText}>Drag & Drop a file here or Click to select</p>
                        </>}
                    </label>
                  </div>
                  {error && <p className={`text-danger`}>{error}</p>}
                  {errorMessages.file && (
                    <p className={`text-danger`}>{errorMessages.file}</p>
                  )}
                </div>
              </div>
            </div>
            <button
              className={`btn-primary`}
              type="button"
              onClick={handleBulkRegistration}
              disabled={disabledBtn}
            >
              {isLotteryCreating ? <BeatLoader grade="#ffffff" /> : `Create`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkRegistration;
